import jsPDF from 'jspdf';
import { formatAmount, formatDecimal, formatActivityLabel, capitalizeList } from './formatters';
import { compressImageSrc } from './image-compression';
import { COMPANY, getCompanyContactLines } from './company-info';
import bauwerkplusFooterLogoSrc from '@assets/image_1776364180891.png';
import hoferLogoSrc from '@assets/Screenshot_2026-04-16_190336_1776453016229.png';
import ortnerLogoSrc from '@assets/Screenshot_2026-04-16_190437_1776453016228.png';
import headerBannerSrc from '@assets/Briefpapierkopf_heade_BW+_1776491453080.png';
import headerLogoOverlaySrc from '@assets/Bauwerk_Plus_Logo_PNG_Transparent_RGB_1776517907314.png';

export interface QuoteData {
  projectName: string;
  customerName: string;
  customerAddress: string;
  quoteNumber?: string;
  items: Array<{
    description: string;
    quantity: number;
    unit: string;
    unitPrice: number;
    total: number;
    sourceLabel?: string;
    /** Vergleichsvariante – wird im PDF angezeigt, aber NICHT in die Summe gerechnet. */
    variant?: boolean;
    /** Optionaler Beschreibungstext (z.B. aus Foto-Analyse übernommen) – wird unter der Positionszeile ausgegeben. */
    beschreibung?: string;
    /** Optionale Foto-Vorschauen, die zu dieser Position gehören. Werden im PDF
     *  als kleine Thumbnail-Reihe unter der Beschreibung gerendert. */
    photoPreviews?: Array<{ preview: string; label?: string }>;
  }>;
  photos?: Array<{ preview?: string; file: File }>;
  measurements?: Array<{
    photoName: string;
    fileName?: string;
    preview?: string;
    /** Erfasste Menge (Einheit s. `unit`). */
    amount: number;
    /** Einheit der Menge (z.B. m², Stück, lfm, …). */
    unit: string;
  }>;
  analysisResults?: Array<{
    material: string;
    condition: string;
    recommendations: string[];
  }>;
  perPhoto?: Array<{
    photoLabel: string;
    fileName?: string;
    /** Erfasste Menge des Bildes. */
    amount?: number;
    /** Einheit der Menge. */
    unit?: string;
    /** Optionale Bildquelle (Data-URL oder URL) für ein Vorschaubild im Header. */
    preview?: string;
    positions: Array<{
      name: string;
      qty: number;
      unit: string;
      ep: number;
      total: number;
      /** Vergleichsvariante – wird angezeigt, aber NICHT in die Zwischensumme gerechnet. */
      variant?: boolean;
    }>;
    subtotal: number;
  }>;
  notes: string;
  validUntil: string;
  subtotal: number;
  tax: number;
  total: number;
  /** Projektbeschreibung aus Schritt 1 + 2 (Stammdaten). Optional. */
  projectDetails?: {
    activity?: string;            // Tätigkeit (project.type)
    objektbeschaffenheit?: string[];
    massnahmen?: string[];
    geschosszahl?: number | null;
    hoehe?: number | null;        // in Metern
    menge?: number | null;
    mengeneinheit?: string;
    /** Freitext-Leistungs-/Projektbeschreibung aus Schritt 1.
     *  Wird im Stammdaten-Block direkt vor der Leistungspositionen-Tabelle
     *  als eigener Absatz gerendert (Plain Text inkl. Bullet-Striche). */
    description?: string;
  };
}

function getImageFormat(dataUrl: string): string {
  if (dataUrl.startsWith('data:image/png')) return 'PNG';
  if (dataUrl.startsWith('data:image/webp')) return 'WEBP';
  return 'JPEG';
}

const TEAL_R = 45, TEAL_G = 90, TEAL_B = 90;
const ORANGE_R = 255, ORANGE_G = 140, ORANGE_B = 0;
const MINT_R = 184, MINT_G = 212, MINT_B = 212;
const ALT_R = 242, ALT_G = 247, ALT_B = 247;

export interface PdfLayoutOptions {
  /** Seitenrand (links/rechts und Fallback oben). Default 15 mm. */
  margin?: number;
  /** Abstand zwischen Header-Banner und Inhalt. Default 2 mm. */
  headerContentGap?: number;
  /** Abstand unterhalb des Adress-/Infobox-Blocks. Default 20 mm (2 cm). */
  addressBottomGap?: number;
  /** Zusätzlicher Abstand vor dem „ANGEBOT"-Titel. Default 0 mm. */
  titleTopGap?: number;
}

export const DEFAULT_PDF_LAYOUT: Required<PdfLayoutOptions> = {
  margin: 15,
  headerContentGap: 2,
  addressBottomGap: 20,
  titleTopGap: 0,
};

export class PDFGenerator {
  private pdf!: jsPDF;
  private pageHeight!: number;
  private pageWidth!: number;
  private currentY!: number;
  private margin = DEFAULT_PDF_LAYOUT.margin;
  private layout: Required<PdfLayoutOptions> = { ...DEFAULT_PDF_LAYOUT };
  private contentWidth!: number;
  private logo2DataUrl: string | null = null;
  private hoferLogoDataUrl: string | null = null;
  private hoferLogoAspect: number | null = null;
  private ortnerLogoDataUrl: string | null = null;
  private ortnerLogoAspect: number | null = null;
  private headerLogoDataUrl: string | null = null;
  private bauwerkplusFooterLogoDataUrl: string | null = null;
  // Oberer, zugeschnittener Bereich der Header-Vorlage (Welle + Logo + Kontakt),
  // ohne das in der Vorlage enthaltene "ANGEBOT". Wird in addHeader() seitenbreit
  // proportional skaliert eingefügt.
  private headerBannerDataUrl: string | null = null;
  private headerBannerAspect: number | null = null;
  private headerLogoOverlayDataUrl: string | null = null;
  private headerLogoOverlayAspect: number | null = null;

  private readonly cols = {
    pos:   { x: 0,   w: 8  },
    desc:  { x: 8,   w: 80 },
    qty:   { x: 88,  w: 22 },
    unit:  { x: 110, w: 20 },
    ep:    { x: 130, w: 25 },
    total: { x: 155, w: 25 },
  };

  private col(name: keyof typeof this.cols) {
    const c = this.cols[name];
    return { x: this.margin + c.x, w: c.w };
  }

  private newDoc() {
    this.pdf = new jsPDF({ unit: 'mm', format: 'a4' });
    this.pageHeight = this.pdf.internal.pageSize.height;
    this.pageWidth = this.pdf.internal.pageSize.width;
    this.contentWidth = this.pageWidth - 2 * this.margin;
    this.currentY = 0;
  }

  private async loadLogo(): Promise<void> {
    await Promise.allSettled([
      compressImageSrc(COMPANY.logoUrl2, 300, 0.9)
        .then(d => { this.logo2DataUrl = d; })
        .catch((e) => { console.warn("Logo2-Komprimierung fehlgeschlagen:", e); this.logo2DataUrl = null; }),
      compressImageSrc(COMPANY.headerLogoUrl, 400, 0.92)
        .then(d => { this.headerLogoDataUrl = d; })
        .catch((e) => { console.warn("Header-Logo-Komprimierung fehlgeschlagen:", e); this.headerLogoDataUrl = null; }),
      compressImageSrc(bauwerkplusFooterLogoSrc, 300, 0.9)
        .then(d => { this.bauwerkplusFooterLogoDataUrl = d; })
        .catch((e) => { console.warn("Bauwerk-Footer-Logo-Komprimierung fehlgeschlagen:", e); this.bauwerkplusFooterLogoDataUrl = null; }),
      this.loadPartnerLogo(hoferLogoSrc)
        .then(({ dataUrl, aspect }) => {
          this.hoferLogoDataUrl = dataUrl;
          this.hoferLogoAspect = aspect;
        })
        .catch((e) => { console.warn("Hofer-Footer-Logo-Komprimierung fehlgeschlagen:", e); this.hoferLogoDataUrl = null; this.hoferLogoAspect = null; }),
      this.loadPartnerLogo(ortnerLogoSrc)
        .then(({ dataUrl, aspect }) => {
          this.ortnerLogoDataUrl = dataUrl;
          this.ortnerLogoAspect = aspect;
        })
        .catch((e) => { console.warn("Ortner-Footer-Logo-Komprimierung fehlgeschlagen:", e); this.ortnerLogoDataUrl = null; this.ortnerLogoAspect = null; }),
      this.loadHeaderBanner()
        .catch((e) => { console.warn("Header-Banner-Vorbereitung fehlgeschlagen:", e); this.headerBannerDataUrl = null; }),
      this.loadHeaderLogoOverlay()
        .catch((e) => { console.warn("Header-Logo-Overlay-Vorbereitung fehlgeschlagen:", e); this.headerLogoOverlayDataUrl = null; }),
    ]);
  }

  /**
   * Lädt ein Partner-Logo und ermittelt sein natürliches Seitenverhältnis,
   * damit es im Footer ohne Verzerrung skaliert werden kann.
   */
  private async loadPartnerLogo(src: string): Promise<{ dataUrl: string; aspect: number }> {
    const aspect = await new Promise<number>((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        const w = img.naturalWidth || img.width;
        const h = img.naturalHeight || img.height;
        if (!w || !h) {
          reject(new Error('Ungültige Logo-Dimensionen'));
          return;
        }
        resolve(w / h);
      };
      img.onerror = () => reject(new Error('Partner-Logo konnte nicht geladen werden'));
      img.src = src;
    });
    const dataUrl = await compressImageSrc(src, 400, 0.92);
    return { dataUrl, aspect };
  }

  /**
   * Lädt das Briefpapierkopf-Bild und beschneidet es auf ein bannerförmiges
   * Seitenverhältnis (oben), damit der dunkle Welleneindruck erhalten bleibt
   * und kein übergroßer Weißraum unterhalb der Welle übrig bleibt.
   */
  private async loadHeaderBanner(): Promise<void> {
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const i = new Image();
      i.onload = () => resolve(i);
      i.onerror = () => reject(new Error('Header-Banner konnte nicht geladen werden'));
      i.src = headerBannerSrc;
    });
    const sw = img.naturalWidth || img.width;
    const sh = img.naturalHeight || img.height;
    if (!sw || !sh) throw new Error('Ungültige Header-Banner-Dimensionen');

    // Zielseitenverhältnis ~3.0 (z. B. 210mm x 70mm). Die ursprüngliche
    // Vorlage hat ~1.5; wir behalten den oberen Teil mit der Welle.
    const targetAspect = 3.0;
    let cropW = sw;
    let cropH = sh;
    const sourceAspect = sw / sh;
    if (sourceAspect < targetAspect) {
      cropH = Math.round(sw / targetAspect);
    } else {
      cropW = Math.round(sh * targetAspect);
    }

    const canvas = document.createElement('canvas');
    canvas.width = cropW;
    canvas.height = cropH;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Canvas-Kontext nicht verfügbar');
    ctx.drawImage(img, 0, 0, cropW, cropH, 0, 0, cropW, cropH);

    this.headerBannerAspect = cropW / cropH;
    this.headerBannerDataUrl = canvas.toDataURL('image/jpeg', 0.92);
  }

  /**
   * Lädt das transparente BauwerkPlus-Logo, das über den Briefkopf-Banner
   * gelegt wird, und ermittelt das natürliche Seitenverhältnis.
   */
  private async loadHeaderLogoOverlay(): Promise<void> {
    const aspect = await new Promise<number>((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        const w = img.naturalWidth || img.width;
        const h = img.naturalHeight || img.height;
        if (!w || !h) {
          reject(new Error('Ungültige Logo-Overlay-Dimensionen'));
          return;
        }
        resolve(w / h);
      };
      img.onerror = () => reject(new Error('Logo-Overlay konnte nicht geladen werden'));
      img.src = headerLogoOverlaySrc;
    });
    const dataUrl = await new Promise<string>((resolve, reject) => {
      fetch(headerLogoOverlaySrc)
        .then(res => res.blob())
        .then(blob => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result as string);
          reader.onerror = () => reject(new Error('Logo-Overlay konnte nicht gelesen werden'));
          reader.readAsDataURL(blob);
        })
        .catch(reject);
    });
    this.headerLogoOverlayAspect = aspect;
    this.headerLogoOverlayDataUrl = dataUrl;
  }

  private checkPageBreak(needed: number) {
    if (this.currentY + needed > this.pageHeight - 34) {
      this.pdf.addPage();
      this.currentY = this.margin;
    }
  }

  async generateQuotePDF(data: QuoteData, options?: PdfLayoutOptions): Promise<Blob> {
    this.layout = { ...DEFAULT_PDF_LAYOUT, ...(options ?? {}) };
    this.margin = this.layout.margin;
    this.newDoc();
    await this.loadLogo();

    this.addHeader();
    this.addAddressSection(data);
    this.addProjectDetails(data);

    // Leistungspositionen beginnen IMMER auf einer frischen Seite (im
    // Standardfall Seite 2). Damit erscheinen die grüne LEISTUNGSPOSITIONEN-
    // Kopfleiste und die Tabellen-Kopfzeile nie unten auf Seite 1, sondern
    // konsistent oben auf einer neuen Seite — analog zum Briefkopf-Banner
    // auf Seite 1, aber ohne erneuten „ANGEBOT"-Titel.
    this.pdf.addPage();
    this.currentY = 0;
    this.addHeaderBanner();
    await this.addItemsTable(data);

    if (data.perPhoto && data.perPhoto.length > 0) {
      await this.addPerPhotoBreakdown(data);
    }

    if (data.measurements && data.measurements.length > 0) {
      this.pdf.addPage();
      this.currentY = this.margin;
      await this.addMeasurementPhotos(data.measurements);
    }
    if (data.photos && data.photos.length > 0) {
      this.pdf.addPage();
      this.currentY = this.margin;
      await this.addPhotos(data.photos);
    }
    if (data.analysisResults && data.analysisResults.length > 0) {
      this.addAnalysisResults(data.analysisResults);
    }
    if (data.notes) {
      this.addNotes(data.notes);
    }

    this.addFooterAllPages();
    return this.pdf.output('blob');
  }

  /**
   * Rendert den Briefkopf-Banner (Welle + BauwerkPlus-Logo + Kontaktzeile)
   * am oberen Seitenrand der aktuellen Seite. Setzt `currentY` auf die
   * Höhe direkt unterhalb des Banners (inkl. konfiguriertem Abstand).
   * Der „ANGEBOT"-Titel wird hier NICHT gerendert — dafür siehe
   * `addHeader()`. So kann derselbe Banner z.B. auf Seite 2 oben über
   * den Leistungspositionen wiederholt werden, ohne den Titel doppelt zu
   * setzen.
   * @returns true wenn der Banner gezeichnet werden konnte, sonst false
   *   (z.B. weil die Vorlage nicht geladen wurde) — in dem Fall wurde
   *   `currentY` lediglich auf den Standard-Top-Margin gesetzt.
   */
  private addHeaderBanner(): boolean {
    if (this.headerBannerDataUrl && this.headerBannerAspect) {
      const bannerW = this.pageWidth;
      const bannerH = bannerW / this.headerBannerAspect;
      try {
        this.pdf.addImage(
          this.headerBannerDataUrl,
          getImageFormat(this.headerBannerDataUrl),
          0, 0, bannerW, bannerH,
          undefined, 'FAST'
        );

        // Logo-Overlay: groß und prominent links im Banner, leicht unterhalb
        // der Welle platziert, damit der Eindruck der Vorlage erhalten bleibt.
        if (this.headerLogoOverlayDataUrl && this.headerLogoOverlayAspect) {
          const logoW = 66;
          const logoH = logoW / this.headerLogoOverlayAspect;
          const logoX = this.margin;
          const logoY = Math.max(bannerH * 0.32, (bannerH - logoH) / 2 - 2);
          this.pdf.addImage(
            this.headerLogoOverlayDataUrl,
            getImageFormat(this.headerLogoOverlayDataUrl),
            logoX, logoY, logoW, logoH,
            undefined, 'FAST'
          );

          // Kontaktzeile rechts neben dem Logo, auf Höhe der Logo-Unterkante.
          // Rechtsbündig zum Seitenrand wie in der Briefpapier-Vorlage.
          this.pdf.setTextColor(TEAL_R, TEAL_G, TEAL_B);
          this.pdf.setFont('helvetica', 'normal');
          this.pdf.setFontSize(7);
          const contactLines = getCompanyContactLines();
          const lineGap = 3;
          const lastContactY = logoY + logoH - 1.5;
          const firstContactY = lastContactY - (contactLines.length - 1) * lineGap;
          contactLines.forEach((line, i) => {
            this.pdf.text(
              line,
              this.pageWidth - this.margin,
              firstContactY + i * lineGap,
              { align: 'right' }
            );
          });

          // Feine teal Trennlinie zwischen Logo-Bereich und Inhalt.
          this.pdf.setDrawColor(TEAL_R, TEAL_G, TEAL_B);
          this.pdf.setLineWidth(0.25);
          this.pdf.line(
            logoX + logoW + 4,
            lastContactY + 1.6,
            this.pageWidth - this.margin,
            lastContactY + 1.6
          );
        }

        this.currentY = bannerH + this.layout.headerContentGap;
        return true;
      } catch {
        /* Fällt unten auf einen schlichten Top-Margin zurück */
      }
    }

    // Fallback ohne Header-Bild: nur sauberen Top-Margin setzen, damit
    // keine Teal-Leiste oder andere Platzhalter-Grafik erscheint.
    this.currentY = this.margin;
    return false;
  }

  private addHeader() {
    // Briefpapierkopf-Bild (dunkle Welle) plus „ANGEBOT"-Titel auf Seite 1.
    const drawn = this.addHeaderBanner();
    if (!drawn) return;

    // „ANGEBOT"-Titel: deutlich größer mit klarer oranger Akzentlinie.
    this.pdf.setTextColor(TEAL_R, TEAL_G, TEAL_B);
    this.pdf.setFont('helvetica', 'bold');
    this.pdf.setFontSize(30);
    const angebotY = this.currentY + 10 + this.layout.titleTopGap;
    this.pdf.text('ANGEBOT', this.margin, angebotY);
    this.pdf.setDrawColor(ORANGE_R, ORANGE_G, ORANGE_B);
    this.pdf.setLineWidth(1.4);
    this.pdf.line(this.margin, angebotY + 2.4, this.margin + 42, angebotY + 2.4);
    this.currentY = angebotY + 7;
  }

  private addAddressSection(data: QuoteData) {
    const startY = this.currentY;

    this.pdf.setTextColor(160, 160, 160);
    this.pdf.setFontSize(6.5);
    this.pdf.setFont('helvetica', 'normal');
    const senderLines = getCompanyContactLines();
    const senderLineGap = 2.6;
    senderLines.forEach((line, i) => {
      this.pdf.text(line, this.margin, this.currentY + i * senderLineGap);
    });
    const lastSenderY = this.currentY + (senderLines.length - 1) * senderLineGap;

    this.pdf.setDrawColor(210, 210, 210);
    this.pdf.setLineWidth(0.2);
    this.pdf.line(this.margin, lastSenderY + 1.2, this.margin + 95, lastSenderY + 1.2);

    this.currentY = lastSenderY + 10.6;

    // Maximal verfügbare Breite für Kunden-Name/Adresse, damit nichts in
    // die rechte Infobox (startet bei margin + 95) hineinläuft.
    const addressMaxW = 88;

    this.pdf.setTextColor(0, 0, 0);
    this.pdf.setFontSize(13);
    this.pdf.setFont('helvetica', 'bold');
    const nameLines = this.pdf.splitTextToSize(data.customerName || '—', addressMaxW);
    nameLines.slice(0, 2).forEach((line: string) => {
      this.pdf.text(line, this.margin, this.currentY);
      this.currentY += 5.6;
    });
    this.currentY += 0.4;

    if (data.customerAddress) {
      this.pdf.setFont('helvetica', 'normal');
      this.pdf.setFontSize(10);
      data.customerAddress.split('\n').forEach(line => {
        if (!line.trim()) return;
        const wrapped = this.pdf.splitTextToSize(line, addressMaxW);
        wrapped.forEach((wl: string) => {
          this.pdf.text(wl, this.margin, this.currentY);
          this.currentY += 4.8;
        });
      });
    }

    const boxX = this.margin + 95;
    const boxW = 85;
    const boxY = startY - 1;
    const infoRows: [string, string][] = [
      ['Angebots-Nr.:', data.quoteNumber ?? '—'],
      ['Datum:', new Date().toLocaleDateString('de-DE')],
      ['Gültig bis:', new Date(data.validUntil).toLocaleDateString('de-DE')],
      ['Projekt:', data.projectName || '—'],
    ];
    const rowH = 6.5;
    const boxH = 9 + infoRows.length * rowH;

    this.pdf.setFillColor(248, 251, 251);
    this.pdf.setDrawColor(MINT_R, MINT_G, MINT_B);
    this.pdf.setLineWidth(0.4);
    this.pdf.roundedRect(boxX, boxY, boxW, boxH, 2, 2, 'FD');

    this.pdf.setFillColor(TEAL_R, TEAL_G, TEAL_B);
    this.pdf.roundedRect(boxX, boxY, boxW, 8, 2, 2, 'F');
    this.pdf.rect(boxX, boxY + 5, boxW, 3, 'F');
    this.pdf.setTextColor(255, 255, 255);
    this.pdf.setFontSize(7.5);
    this.pdf.setFont('helvetica', 'bold');
    this.pdf.text('ANGEBOTSINFORMATIONEN', boxX + boxW / 2, boxY + 5.5, { align: 'center' });

    let ry = boxY + 12;
    infoRows.forEach(([label, value]) => {
      this.pdf.setTextColor(100, 110, 110);
      this.pdf.setFontSize(8);
      this.pdf.setFont('helvetica', 'normal');
      this.pdf.text(label, boxX + 4, ry);

      this.pdf.setTextColor(20, 20, 20);
      this.pdf.setFont('helvetica', 'bold');
      const val = String(value).length > 24 ? String(value).slice(0, 22) + '…' : String(value);
      this.pdf.text(val, boxX + boxW - 4, ry, { align: 'right' });
      ry += rowH;
    });

    const boxBottom = boxY + boxH;
    if (this.currentY < boxBottom) this.currentY = boxBottom;
    this.currentY += this.layout.addressBottomGap;
  }

  private addProjectDetails(data: QuoteData) {
    const d = data.projectDetails;
    if (!d) return;
    const hasObjekt = (d.objektbeschaffenheit ?? []).length > 0;
    const hasMassn = (d.massnahmen ?? []).length > 0;
    const hasGeschoss = d.geschosszahl != null;
    const hasHoehe = d.hoehe != null;
    const hasMenge = d.menge != null;
    const descriptionText = (d.description ?? '').trim();
    const hasDescription = descriptionText.length > 0;
    if (
      !d.activity && !hasObjekt && !hasMassn && !hasGeschoss && !hasHoehe && !hasMenge && !hasDescription
    ) return;

    this.checkPageBreak(40);

    // Tätigkeit als prominente Überschrift
    if (d.activity) {
      this.pdf.setTextColor(TEAL_R, TEAL_G, TEAL_B);
      this.pdf.setFont('helvetica', 'bold');
      this.pdf.setFontSize(14);
      this.pdf.text(formatActivityLabel(d.activity), this.margin, this.currentY);
      this.pdf.setDrawColor(ORANGE_R, ORANGE_G, ORANGE_B);
      this.pdf.setLineWidth(0.8);
      this.pdf.line(this.margin, this.currentY + 1.6, this.margin + 28, this.currentY + 1.6);
      this.currentY += 6.5;
    }

    // Schlüssel-Wert-Block, kompakt
    const rows: Array<[string, string]> = [];
    if (hasObjekt) rows.push(['Objektbeschaffenheit', capitalizeList(d.objektbeschaffenheit).join(', ')]);
    if (hasMassn) rows.push(['Maßnahmen', capitalizeList(d.massnahmen).join(', ')]);
    if (hasGeschoss) rows.push(['Geschosszahl', String(d.geschosszahl)]);
    if (hasHoehe) rows.push(['Höhe', `${formatDecimal(d.hoehe as number)} m`]);
    if (hasMenge) {
      const einheit = d.mengeneinheit ? ` ${d.mengeneinheit}` : '';
      rows.push(['Mengenausmaß', `${formatDecimal(d.menge as number)}${einheit}`]);
    }

    if (rows.length > 0) {
      const labelW = 38;
      const valueW = this.contentWidth - labelW - 2;
      this.pdf.setFontSize(9);
      rows.forEach(([label, value]) => {
        const wrapped = this.pdf.splitTextToSize(value, valueW) as string[];
        const blockH = Math.max(4.6, wrapped.length * 4.4) + 0.4;
        this.checkPageBreak(blockH + 2);
        this.pdf.setTextColor(100, 110, 110);
        this.pdf.setFont('helvetica', 'normal');
        this.pdf.text(label + ':', this.margin, this.currentY);
        this.pdf.setTextColor(20, 20, 20);
        this.pdf.setFont('helvetica', 'bold');
        wrapped.forEach((wl, idx) => {
          this.pdf.text(wl, this.margin + labelW, this.currentY + idx * 4.4);
        });
        this.currentY += blockH;
      });
    }

    // Projektbeschreibung als eigenständiger Absatz unter den Schlüssel/Wert-
    // Zeilen. Mehrzeilige Eingaben (mit \n) werden zeilenweise umbrochen,
    // jede Eingabezeile wird zusätzlich auf die Inhaltsbreite gewrapped.
    // Page-Break-Verhalten: Die Überschrift bleibt mit mindestens einer
    // Folgezeile zusammen; pro Zeile wird checkPageBreak gerufen, sodass
    // lange Texte sauber auf die nächste Seite umbrechen.
    if (hasDescription) {
      // Dezente Abtrennung zu den Schlüssel/Wert-Zeilen oben.
      if (rows.length > 0 || d.activity) this.currentY += 2;

      this.checkPageBreak(12);
      this.pdf.setFontSize(9);
      this.pdf.setTextColor(100, 110, 110);
      this.pdf.setFont('helvetica', 'normal');
      this.pdf.text('Leistungsbeschreibung:', this.margin, this.currentY);
      this.currentY += 4.4;

      this.pdf.setTextColor(20, 20, 20);
      this.pdf.setFont('helvetica', 'normal');
      const inputLines = descriptionText.split(/\r?\n/);
      const lineH = 4.4;
      inputLines.forEach((rawLine) => {
        const line = rawLine.length > 0 ? rawLine : ' ';
        const wrapped = this.pdf.splitTextToSize(line, this.contentWidth) as string[];
        wrapped.forEach((wl) => {
          this.checkPageBreak(lineH + 2);
          this.pdf.text(wl, this.margin, this.currentY);
          this.currentY += lineH;
        });
      });
    }

    this.currentY += 3;
  }

  private addSectionHeader(title: string) {
    this.checkPageBreak(14);
    this.pdf.setFillColor(TEAL_R, TEAL_G, TEAL_B);
    this.pdf.rect(this.margin, this.currentY, this.contentWidth, 7.5, 'F');
    this.pdf.setTextColor(255, 255, 255);
    this.pdf.setFontSize(9);
    this.pdf.setFont('helvetica', 'bold');
    this.pdf.text(title.toUpperCase(), this.margin + 4, this.currentY + 5.2);
    this.currentY += 10;
  }

  private async addItemsTable(data: QuoteData) {
    this.addSectionHeader('Leistungspositionen');

    const rowH = 7;
    const tableY = this.currentY;

    // Foto-Thumbnails pro Position vorab parallel komprimieren, damit das
    // anschließende Rendering synchron in der forEach-Schleife passieren kann.
    const THUMB_SIZE_MM = 12;
    const THUMB_GAP_MM = 1.5;
    const THUMB_ROW_GAP_MM = 1.5;
    const THUMB_MAX_PER_ITEM = 6;
    type ItemThumb = { src: string; drawW: number; drawH: number };
    const itemThumbnails: Array<ItemThumb[]> = await Promise.all(
      data.items.map(async (item) => {
        const previews = (item.photoPreviews ?? []).slice(0, THUMB_MAX_PER_ITEM);
        if (previews.length === 0) return [];
        const compressed = await Promise.all(
          previews.map(async (p) => {
            try {
              const src = await this.compressImage(p.preview, 192, 0.7);
              const dims = await this.getImageDimensions(src);
              let drawW = THUMB_SIZE_MM;
              let drawH = THUMB_SIZE_MM;
              if (dims && dims.width > 0 && dims.height > 0) {
                const ratio = dims.width / dims.height;
                if (ratio >= 1) drawH = THUMB_SIZE_MM / ratio;
                else drawW = THUMB_SIZE_MM * ratio;
              }
              return { src, drawW, drawH } as ItemThumb;
            } catch {
              return null;
            }
          })
        );
        return compressed.filter((t): t is ItemThumb => t !== null);
      })
    );

    this.pdf.setFillColor(TEAL_R, TEAL_G, TEAL_B);
    this.pdf.rect(this.margin, tableY, this.contentWidth, rowH, 'F');

    this.pdf.setTextColor(255, 255, 255);
    this.pdf.setFontSize(8.5);
    this.pdf.setFont('helvetica', 'bold');

    const posC = this.col('pos');
    const descC = this.col('desc');
    const qtyC = this.col('qty');
    const unitC = this.col('unit');
    const epC = this.col('ep');
    const totalC = this.col('total');

    const headerY = tableY + 4.8;
    this.pdf.text('Pos.', posC.x + 1, headerY);
    this.pdf.text('Beschreibung', descC.x, headerY);
    this.pdf.text('Menge', qtyC.x + qtyC.w, headerY, { align: 'right' });
    this.pdf.text('Einheit', unitC.x + 1, headerY);
    this.pdf.text('EP (€)', epC.x + epC.w, headerY, { align: 'right' });
    this.pdf.text('Gesamt (€)', totalC.x + totalC.w, headerY, { align: 'right' });

    this.currentY = tableY + rowH;

    data.items.forEach((item, index) => {
      const hasSource = !!item.sourceLabel;
      const beschreibungText = (item.beschreibung ?? '').trim();
      const hasBeschreibung = beschreibungText.length > 0;
      const beschreibungWidth = this.contentWidth - (descC.x - this.margin) - 2;
      const beschreibungLineH = 3.4;
      const beschreibungLines = hasBeschreibung
        ? (this.pdf.splitTextToSize(beschreibungText, beschreibungWidth) as string[])
        : [];
      const beschreibungBlockH = hasBeschreibung
        ? beschreibungLines.length * beschreibungLineH + 2
        : 0;
      const thumbs = itemThumbnails[index] ?? [];
      const totalPreviews = item.photoPreviews?.length ?? 0;
      const extraCount = Math.max(0, totalPreviews - thumbs.length);
      const thumbBlockH = thumbs.length > 0
        ? THUMB_SIZE_MM + THUMB_ROW_GAP_MM + (extraCount > 0 ? 3.4 : 0) + 1
        : 0;
      const itemH = (hasSource ? rowH + 4 : rowH) + beschreibungBlockH + thumbBlockH;
      this.checkPageBreak(itemH + 2);

      if (index % 2 === 1) {
        this.pdf.setFillColor(ALT_R, ALT_G, ALT_B);
        this.pdf.rect(this.margin, this.currentY, this.contentWidth, itemH, 'F');
      }

      const cellY = this.currentY + 4.8;

      this.pdf.setTextColor(90, 90, 90);
      this.pdf.setFontSize(8.5);
      this.pdf.setFont('helvetica', 'normal');
      this.pdf.text(String(index + 1), posC.x + 1, cellY);

      const descSuffix = item.variant ? '  (Variante)' : '';
      const descLines = this.pdf.splitTextToSize((item.description || '—') + descSuffix, descC.w - 2);
      this.pdf.setTextColor(item.variant ? 150 : 20, item.variant ? 110 : 20, item.variant ? 30 : 20);
      this.pdf.text(descLines[0] + (descLines.length > 1 ? ' …' : ''), descC.x, cellY);

      if (hasSource) {
        const sourceTrunc = item.sourceLabel!.length > 48 ? item.sourceLabel!.slice(0, 48) + '…' : item.sourceLabel!;
        this.pdf.setFontSize(6.5);
        this.pdf.setTextColor(80, 130, 110);
        this.pdf.text(`↳ ${sourceTrunc}`, descC.x + 1, cellY + 3.8);
      }

      this.pdf.setFontSize(8.5);
      this.pdf.setTextColor(50, 50, 50);
      this.pdf.text(
        formatDecimal(item.quantity, 2, { minFractionDigits: 0 }),
        qtyC.x + qtyC.w, cellY, { align: 'right' }
      );
      this.pdf.text(item.unit, unitC.x + 1, cellY);
      this.pdf.text(formatAmount(item.unitPrice), epC.x + epC.w, cellY, { align: 'right' });

      if (item.variant) {
        this.pdf.setTextColor(150, 110, 30);
        this.pdf.setFont('helvetica', 'italic');
      } else {
        this.pdf.setTextColor(20, 20, 20);
        this.pdf.setFont('helvetica', 'bold');
      }
      this.pdf.text(formatAmount(item.total), totalC.x + totalC.w, cellY, { align: 'right' });

      if (hasBeschreibung) {
        const baseY = this.currentY + (hasSource ? rowH + 4 : rowH);
        this.pdf.setFont('helvetica', 'italic');
        this.pdf.setFontSize(7.5);
        this.pdf.setTextColor(70, 80, 80);
        beschreibungLines.forEach((ln, i) => {
          this.pdf.text(ln, descC.x, baseY + i * beschreibungLineH + 2.4);
        });
      }

      if (thumbs.length > 0) {
        const thumbBaseY = this.currentY + (hasSource ? rowH + 4 : rowH) + beschreibungBlockH;
        let tx = descC.x;
        thumbs.forEach((t) => {
          const ix = tx + (THUMB_SIZE_MM - t.drawW) / 2;
          const iy = thumbBaseY + (THUMB_SIZE_MM - t.drawH) / 2;
          try {
            this.pdf.addImage(t.src, getImageFormat(t.src), ix, iy, t.drawW, t.drawH);
          } catch { /* ignore */ }
          tx += THUMB_SIZE_MM + THUMB_GAP_MM;
        });
        if (extraCount > 0) {
          this.pdf.setFont('helvetica', 'normal');
          this.pdf.setFontSize(7);
          this.pdf.setTextColor(110, 120, 120);
          this.pdf.text(
            `+${extraCount} weitere`,
            tx,
            thumbBaseY + THUMB_SIZE_MM - 1.5,
          );
        }
      }

      this.pdf.setDrawColor(210, 215, 215);
      this.pdf.setLineWidth(0.15);
      this.pdf.line(this.margin, this.currentY + itemH, this.margin + this.contentWidth, this.currentY + itemH);

      this.currentY += itemH;
    });

    this.currentY += 8;

    // Wenn eine Foto-Aufschlüsselung vorhanden ist, wird der finale
    // Summenblock erst nach den Foto-Blöcken gerendert (Leistungspositionen
    // + Foto-Zwischensummen = Netto). Hier deshalb überspringen.
    if (data.perPhoto && data.perPhoto.length > 0) {
      return;
    }

    this.renderTotalsBox(data.subtotal, data.tax, data.total);
  }

  private renderTotalsBox(subtotal: number, tax: number, total: number) {
    this.checkPageBreak(36);

    const sumW = 88;
    const sumX = this.margin + this.contentWidth - sumW;
    const sumY = this.currentY;

    this.pdf.setFillColor(250, 252, 252);
    this.pdf.setDrawColor(MINT_R, MINT_G, MINT_B);
    this.pdf.setLineWidth(0.4);
    this.pdf.roundedRect(sumX, sumY, sumW, 22, 2, 2, 'FD');

    this.pdf.setTextColor(70, 80, 80);
    this.pdf.setFontSize(9);
    this.pdf.setFont('helvetica', 'normal');
    this.pdf.text('Nettobetrag:', sumX + 4, sumY + 7);
    this.pdf.text(`${formatAmount(subtotal)} €`, sumX + sumW - 4, sumY + 7, { align: 'right' });

    this.pdf.setDrawColor(200, 210, 210);
    this.pdf.setLineWidth(0.2);
    this.pdf.line(sumX + 4, sumY + 10, sumX + sumW - 4, sumY + 10);

    this.pdf.text('MwSt. (20 %):', sumX + 4, sumY + 16);
    this.pdf.text(`${formatAmount(tax)} €`, sumX + sumW - 4, sumY + 16, { align: 'right' });

    this.currentY = sumY + 22 + 2;

    this.pdf.setFillColor(TEAL_R, TEAL_G, TEAL_B);
    this.pdf.roundedRect(sumX, this.currentY, sumW, 11, 2, 2, 'F');
    this.pdf.setTextColor(255, 255, 255);
    this.pdf.setFontSize(10.5);
    this.pdf.setFont('helvetica', 'bold');
    this.pdf.text('Gesamtbetrag', sumX + 4, this.currentY + 7.2);
    this.pdf.text(`${formatAmount(total)} €`, sumX + sumW - 4, this.currentY + 7.2, { align: 'right' });

    this.currentY += 11 + 12;
  }

  private async addPerPhotoBreakdown(data: QuoteData) {
    const perPhoto = data.perPhoto!;
    this.addSectionHeader('Aufschlüsselung pro Bild');

    // Kompakte Übersicht aller Foto-Zwischensummen vorab als schneller Überblick.
    this.renderPhotoSubtotalsOverview(perPhoto);

    // Detail-Foto-Blöcke beginnen IMMER auf einer frischen Seite (im
    // Standardfall Seite 3). Die kompakte Übersicht oben bleibt damit
    // direkt unter den Leistungspositionen auf Seite 2 sichtbar.
    this.pdf.addPage();
    this.currentY = this.margin;

    const descC = this.col('desc');
    const qtyC = this.col('qty');
    const unitC = this.col('unit');
    const epC = this.col('ep');
    const totalC = this.col('total');

    const rowH = 5.6;
    const THUMB_SIZE = 16; // mm — quadratischer Bildbereich im Header
    const THUMB_PAD = 2;   // Padding um das Thumbnail innerhalb des Header-Bands
    let totalAll = 0;

    // Vorschaubilder vorab komprimieren (parallel) und Maße bestimmen, damit die
    // forEach-Schleife synchron rendern kann und die Seitenumbruch-Berechnung
    // verlässlich ist.
    type ThumbInfo = { src: string; drawW: number; drawH: number } | null;
    const thumbnails: ThumbInfo[] = await Promise.all(
      perPhoto.map(async (e) => {
        if (!e.preview) return null;
        try {
          const src = await this.compressImage(e.preview, 256, 0.75);
          const dims = await this.getImageDimensions(src);
          let drawW = THUMB_SIZE;
          let drawH = THUMB_SIZE;
          if (dims && dims.width > 0 && dims.height > 0) {
            const ratio = dims.width / dims.height;
            if (ratio >= 1) drawH = THUMB_SIZE / ratio;
            else drawW = THUMB_SIZE * ratio;
          }
          return { src, drawW, drawH };
        } catch {
          return null;
        }
      })
    );

    perPhoto.forEach((entry, entryIdx) => {
      totalAll += entry.subtotal;
      const thumb = thumbnails[entryIdx];
      const headerH = thumb ? THUMB_SIZE + THUMB_PAD * 2 : 7;
      const blockH = headerH + 2 + entry.positions.length * rowH + 8;
      this.checkPageBreak(blockH);

      this.pdf.setFillColor(MINT_R, MINT_G, MINT_B);
      this.pdf.rect(this.margin, this.currentY, this.contentWidth, headerH, 'F');

      let textLeftX = this.margin + 3;
      if (thumb) {
        const ix = this.margin + THUMB_PAD + (THUMB_SIZE - thumb.drawW) / 2;
        const iy = this.currentY + THUMB_PAD + (THUMB_SIZE - thumb.drawH) / 2;
        try {
          this.pdf.addImage(thumb.src, getImageFormat(thumb.src), ix, iy, thumb.drawW, thumb.drawH);
        } catch { /* ignore */ }
        textLeftX = this.margin + THUMB_PAD + THUMB_SIZE + 3;
      }

      this.pdf.setTextColor(20, 50, 50);
      this.pdf.setFontSize(8.5);
      this.pdf.setFont('helvetica', 'bold');
      const headLeft = entry.fileName
        ? `${entry.photoLabel} — ${entry.fileName}`
        : entry.photoLabel;
      // Reserviere Platz rechts für die Aufmaß-Anzeige, damit der Header-Text
      // nicht überlappt. Bei vorhandenem Thumbnail darf der Text über 2 Zeilen
      // umgebrochen werden (genug vertikaler Raum), sonst hart auf 1 Zeile
      // gekürzt, da das schmale 7mm-Band keine 2 Zeilen aufnimmt.
      const hasAmount = entry.amount !== undefined && entry.amount > 0;
      const reservedRight = hasAmount ? 34 : 3;
      const availableTextW = Math.max(20, (this.margin + this.contentWidth - reservedRight) - textLeftX);
      const maxLines = thumb ? 2 : 1;
      const splitAll = this.pdf.splitTextToSize(headLeft, availableTextW) as string[];
      const wrapped = splitAll.slice(0, maxLines);
      if (splitAll.length > maxLines && wrapped.length > 0) {
        const last = wrapped[wrapped.length - 1];
        wrapped[wrapped.length - 1] = last.replace(/\s*\S{0,2}$/, '') + '…';
      }
      const lineH = 4.2; // mm bei 8.5pt
      const totalTextH = wrapped.length * lineH;
      const firstLineY = this.currentY + (headerH - totalTextH) / 2 + (lineH - 1.2);
      wrapped.forEach((ln, lnIdx) => {
        this.pdf.text(ln, textLeftX, firstLineY + lnIdx * lineH);
      });
      if (hasAmount) {
        const u = entry.unit ?? 'm²';
        const aufmassY = this.currentY + headerH / 2 + 1.5;
        this.pdf.text(
          `Aufmaß: ${formatDecimal(entry.amount as number, 2)} ${u}`,
          this.margin + this.contentWidth - 3, aufmassY,
          { align: 'right' }
        );
      }
      this.currentY += headerH;

      this.pdf.setTextColor(110, 115, 115);
      this.pdf.setFontSize(7);
      this.pdf.setFont('helvetica', 'normal');
      const headerY = this.currentY + 3.6;
      this.pdf.text('Leistung', descC.x, headerY);
      this.pdf.text('Menge', qtyC.x + qtyC.w, headerY, { align: 'right' });
      this.pdf.text('Einheit', unitC.x + 1, headerY);
      this.pdf.text('EP (€)', epC.x + epC.w, headerY, { align: 'right' });
      this.pdf.text('Gesamt (€)', totalC.x + totalC.w, headerY, { align: 'right' });
      this.currentY += 5;

      entry.positions.forEach((pos, idx) => {
        this.checkPageBreak(rowH + 2);
        if (pos.variant) {
          this.pdf.setFillColor(254, 247, 224); // sehr helles Amber
          this.pdf.rect(this.margin, this.currentY, this.contentWidth, rowH, 'F');
        } else if (idx % 2 === 1) {
          this.pdf.setFillColor(ALT_R, ALT_G, ALT_B);
          this.pdf.rect(this.margin, this.currentY, this.contentWidth, rowH, 'F');
        }
        const cellY = this.currentY + 4;
        this.pdf.setFontSize(8);
        if (pos.variant) {
          this.pdf.setTextColor(150, 110, 30);
          this.pdf.setFont('helvetica', 'italic');
          this.pdf.text(this.truncate(pos.name + '  (Variante)', 60), descC.x, cellY);
        } else {
          this.pdf.setTextColor(30, 30, 30);
          this.pdf.setFont('helvetica', 'normal');
          this.pdf.text(this.truncate(pos.name, 60), descC.x, cellY);
        }
        this.pdf.text(
          formatDecimal(pos.qty, 2, { minFractionDigits: 0 }),
          qtyC.x + qtyC.w, cellY, { align: 'right' }
        );
        this.pdf.text(pos.unit, unitC.x + 1, cellY);
        this.pdf.text(formatAmount(pos.ep), epC.x + epC.w, cellY, { align: 'right' });
        if (pos.variant) {
          this.pdf.setTextColor(150, 110, 30);
          this.pdf.setFont('helvetica', 'italic');
        } else {
          this.pdf.setFont('helvetica', 'bold');
        }
        this.pdf.text(formatAmount(pos.total), totalC.x + totalC.w, cellY, { align: 'right' });
        this.currentY += rowH;
      });

      this.checkPageBreak(8);
      this.pdf.setDrawColor(MINT_R, MINT_G, MINT_B);
      this.pdf.setLineWidth(0.3);
      this.pdf.line(this.margin, this.currentY, this.margin + this.contentWidth, this.currentY);
      this.currentY += 1;
      this.pdf.setTextColor(40, 80, 80);
      this.pdf.setFontSize(8.5);
      this.pdf.setFont('helvetica', 'bold');
      this.pdf.text('Zwischensumme', this.margin + 3, this.currentY + 4.8);
      this.pdf.text(
        `${formatAmount(entry.subtotal)} €`,
        this.margin + this.contentWidth - 3, this.currentY + 4.8,
        { align: 'right' }
      );
      this.currentY += 7;
      this.currentY += 4;
    });

    // Finaler Summenblock: Leistungspositionen + Foto-Zwischensummen = Netto.
    // data.subtotal enthält bereits die Summe aus manuellen Items + perPhoto.
    const itemsSubtotal = data.items.reduce(
      (sum, it) => (it.variant ? sum : sum + it.total),
      0
    );

    this.checkPageBreak(46);

    const sumW = 100;
    const sumX = this.margin + this.contentWidth - sumW;
    const sumY = this.currentY;

    this.pdf.setFillColor(250, 252, 252);
    this.pdf.setDrawColor(MINT_R, MINT_G, MINT_B);
    this.pdf.setLineWidth(0.4);
    this.pdf.roundedRect(sumX, sumY, sumW, 28, 2, 2, 'FD');

    this.pdf.setTextColor(70, 80, 80);
    this.pdf.setFontSize(9);
    this.pdf.setFont('helvetica', 'normal');
    this.pdf.text('Leistungspositionen:', sumX + 4, sumY + 6);
    this.pdf.text(`${formatAmount(itemsSubtotal)} €`, sumX + sumW - 4, sumY + 6, { align: 'right' });

    this.pdf.text('Foto-Zwischensummen:', sumX + 4, sumY + 12);
    this.pdf.text(`${formatAmount(totalAll)} €`, sumX + sumW - 4, sumY + 12, { align: 'right' });

    this.pdf.setDrawColor(200, 210, 210);
    this.pdf.setLineWidth(0.2);
    this.pdf.line(sumX + 4, sumY + 15, sumX + sumW - 4, sumY + 15);

    this.pdf.setFont('helvetica', 'bold');
    this.pdf.text('Nettobetrag:', sumX + 4, sumY + 21);
    this.pdf.text(`${formatAmount(data.subtotal)} €`, sumX + sumW - 4, sumY + 21, { align: 'right' });

    this.pdf.setFont('helvetica', 'normal');
    this.pdf.text('MwSt. (20 %):', sumX + 4, sumY + 26);
    this.pdf.text(`${formatAmount(data.tax)} €`, sumX + sumW - 4, sumY + 26, { align: 'right' });

    this.currentY = sumY + 28 + 2;

    this.pdf.setFillColor(TEAL_R, TEAL_G, TEAL_B);
    this.pdf.roundedRect(sumX, this.currentY, sumW, 11, 2, 2, 'F');
    this.pdf.setTextColor(255, 255, 255);
    this.pdf.setFontSize(10.5);
    this.pdf.setFont('helvetica', 'bold');
    this.pdf.text('Gesamtbetrag', sumX + 4, this.currentY + 7.2);
    this.pdf.text(`${formatAmount(data.total)} €`, sumX + sumW - 4, this.currentY + 7.2, { align: 'right' });

    this.currentY += 11 + 12;
  }

  private renderPhotoSubtotalsOverview(perPhoto: NonNullable<QuoteData['perPhoto']>) {
    const rowH = 5.6;
    const headerH = 7;
    const blockH = headerH + perPhoto.length * rowH + 8;
    this.checkPageBreak(blockH + 4);

    this.pdf.setFillColor(MINT_R, MINT_G, MINT_B);
    this.pdf.rect(this.margin, this.currentY, this.contentWidth, headerH, 'F');
    this.pdf.setTextColor(20, 50, 50);
    this.pdf.setFontSize(8.5);
    this.pdf.setFont('helvetica', 'bold');
    this.pdf.text('Übersicht Foto-Zwischensummen', this.margin + 3, this.currentY + 4.8);
    this.pdf.text(
      'Zwischensumme',
      this.margin + this.contentWidth - 3,
      this.currentY + 4.8,
      { align: 'right' }
    );
    this.currentY += headerH;

    let totalAll = 0;
    perPhoto.forEach((entry, idx) => {
      this.checkPageBreak(rowH + 2);
      if (idx % 2 === 1) {
        this.pdf.setFillColor(ALT_R, ALT_G, ALT_B);
        this.pdf.rect(this.margin, this.currentY, this.contentWidth, rowH, 'F');
      }
      const cellY = this.currentY + 4;
      this.pdf.setTextColor(30, 30, 30);
      this.pdf.setFontSize(8);
      this.pdf.setFont('helvetica', 'normal');
      const label = entry.fileName
        ? `${entry.photoLabel} — ${entry.fileName}`
        : entry.photoLabel;
      this.pdf.text(this.truncate(label, 80), this.margin + 3, cellY);
      this.pdf.setFont('helvetica', 'bold');
      this.pdf.text(
        `${formatAmount(entry.subtotal)} €`,
        this.margin + this.contentWidth - 3,
        cellY,
        { align: 'right' }
      );
      this.currentY += rowH;
      totalAll += entry.subtotal;
    });

    this.pdf.setDrawColor(MINT_R, MINT_G, MINT_B);
    this.pdf.setLineWidth(0.3);
    this.pdf.line(this.margin, this.currentY, this.margin + this.contentWidth, this.currentY);
    this.currentY += 1;
    this.pdf.setTextColor(40, 80, 80);
    this.pdf.setFontSize(8.5);
    this.pdf.setFont('helvetica', 'bold');
    this.pdf.text('Gesamt Foto-Zwischensummen', this.margin + 3, this.currentY + 4.8);
    this.pdf.text(
      `${formatAmount(totalAll)} €`,
      this.margin + this.contentWidth - 3,
      this.currentY + 4.8,
      { align: 'right' }
    );
    this.currentY += 7 + 4;
  }

  private truncate(s: string, max: number): string {
    return s.length > max ? s.slice(0, max - 1) + '…' : s;
  }

  private async addMeasurementPhotos(measurements: Array<{
    photoName: string;
    fileName?: string;
    preview?: string;
    amount: number;
    unit: string;
  }>) {
    this.addSectionHeader('Schadensdokumentation');

    const totalNet = measurements
      .filter((m) => (m.unit ?? 'm²') === 'm²')
      .reduce((sum, m) => sum + m.amount, 0);
    const spacing = 16;
    const captionH = 14;
    // Maximale Bildbox je Spalte; das tatsächliche Bild wird mit dem
    // natürlichen Seitenverhältnis hineinskaliert (wie in der App-Vorschau),
    // damit hochformatige Aufnahmen nicht verzerrt werden.
    const cellW = (this.contentWidth - spacing) / 2;
    const maxImgH = 70;

    type Prepared = {
      m: typeof measurements[number];
      src?: string;
      drawW: number;
      drawH: number;
    };

    const prepare = async (m?: typeof measurements[number]): Promise<Prepared | null> => {
      if (!m) return null;
      const src = m.preview ? await this.compressImage(m.preview) : undefined;
      let drawW = cellW;
      let drawH = maxImgH;
      if (src) {
        const dims = await this.getImageDimensions(src);
        if (dims) {
          const ratio = dims.width / dims.height;
          // Skalierung auf cellW × maxImgH unter Beibehaltung des Seitenverhältnisses
          if (cellW / ratio <= maxImgH) {
            drawW = cellW;
            drawH = cellW / ratio;
          } else {
            drawH = maxImgH;
            drawW = maxImgH * ratio;
          }
        }
      }
      return { m, src, drawW, drawH };
    };

    for (let i = 0; i < measurements.length; i += 2) {
      const left = await prepare(measurements[i]);
      const right = await prepare(measurements[i + 1]);

      const rowImgH = Math.max(left?.drawH ?? 0, right?.drawH ?? 0, 30);
      this.checkPageBreak(rowImgH + captionH + 4);

      const render = (entry: Prepared, x: number) => {
        const { m, src, drawW, drawH } = entry;
        // Bild zentriert in der Zelle platzieren, vertikal an Zellunterkante ausrichten
        const ix = x + (cellW - drawW) / 2;
        const iy = this.currentY + (rowImgH - drawH);
        if (src) {
          try {
            this.pdf.addImage(src, getImageFormat(src), ix, iy, drawW, drawH);
          } catch { /* ignore */ }
        } else {
          this.pdf.setDrawColor(190, 190, 190);
          this.pdf.setFillColor(245, 245, 245);
          this.pdf.rect(ix, iy, drawW, drawH, 'FD');
        }

        const captionY = this.currentY + rowImgH + 6;
        this.pdf.setFontSize(8);
        this.pdf.setFont('helvetica', 'bold');
        this.pdf.setTextColor(20, 20, 20);
        const label = m.fileName ? `${m.photoName}: ${m.fileName}` : m.photoName;
        this.pdf.text(label, x, captionY);

        // Erfasste Menge (mit gespeicherter Einheit) — in Bauwerk-Orange.
        this.pdf.setFont('helvetica', 'bold');
        this.pdf.setTextColor(ORANGE_R, ORANGE_G, ORANGE_B);
        const unitLabel = m.unit ?? 'm²';
        this.pdf.text(`Menge: ${formatDecimal(m.amount, 2)} ${unitLabel}`, x, captionY + 6);
      };

      if (left) render(left, this.margin);
      if (right) render(right, this.margin + cellW + spacing);

      this.currentY += rowImgH + captionH;
    }

    this.checkPageBreak(12);
    this.pdf.setFillColor(TEAL_R, TEAL_G, TEAL_B);
    this.pdf.setTextColor(255, 255, 255);
    this.pdf.roundedRect(this.margin, this.currentY, this.contentWidth, 9, 2, 2, 'F');
    this.pdf.setFontSize(9.5);
    this.pdf.setFont('helvetica', 'bold');
    if (totalNet > 0) {
      this.pdf.text(
        `Gesamtfläche aller Aufmaße (m²): ${formatDecimal(totalNet, 2)} m²`,
        this.margin + 4, this.currentY + 6
      );
    } else {
      this.pdf.text(
        `Aufmaße (${measurements.length} Foto${measurements.length === 1 ? '' : 's'})`,
        this.margin + 4, this.currentY + 6
      );
    }
    this.currentY += 9 + 8;
  }

  /**
   * Liest die natürlichen Pixelmaße eines Bildes (Data-URL oder URL) aus.
   * Wird für die seitenverhältnisgerechte Skalierung der Aufmaß-Fotos genutzt.
   */
  private getImageDimensions(src: string): Promise<{ width: number; height: number } | null> {
    return new Promise((resolve) => {
      try {
        const img = new Image();
        img.onload = () => resolve({ width: img.naturalWidth || img.width, height: img.naturalHeight || img.height });
        img.onerror = () => resolve(null);
        img.src = src;
      } catch {
        resolve(null);
      }
    });
  }

  private async addPhotos(photos: Array<{ preview?: string; file: File }>) {
    this.addSectionHeader('Projektfotos');

    const imgW = 75;
    const imgH = 55;
    const spacing = 16;

    for (let i = 0; i < photos.length; i += 2) {
      const p1 = photos[i];
      const p2 = photos[i + 1];

      this.checkPageBreak(imgH + 24);

      const c1 = p1?.preview ? await this.compressImage(p1.preview) : undefined;
      const c2 = p2?.preview ? await this.compressImage(p2.preview) : undefined;

      const renderPhoto = (p: typeof photos[number], x: number, idx: number, src?: string) => {
        if (src) {
          try {
            this.pdf.addImage(src, getImageFormat(src), x, this.currentY, imgW, imgH);
          } catch { /* ignore */ }
        } else {
          this.pdf.setDrawColor(190, 190, 190);
          this.pdf.setFillColor(245, 245, 245);
          this.pdf.rect(x, this.currentY, imgW, imgH, 'FD');
        }
        this.pdf.setFontSize(8);
        this.pdf.setFont('helvetica', 'normal');
        this.pdf.setTextColor(60, 60, 60);
        this.pdf.text(`Foto ${idx + 1}: ${p.file.name}`, x, this.currentY + imgH + 7);
      };

      if (p1) renderPhoto(p1, this.margin, i, c1);
      if (p2) {
        const rx = this.margin + imgW + spacing;
        if (rx + imgW <= this.margin + this.contentWidth) {
          renderPhoto(p2, rx, i + 1, c2);
        }
      }

      this.currentY += imgH + 20;
    }
  }

  private addAnalysisResults(results: Array<{ material: string; condition: string; recommendations: string[] }>) {
    this.addSectionHeader('KI-Analyse Ergebnisse');

    results.forEach((result, index) => {
      this.checkPageBreak(14 + result.recommendations.length * 5);

      this.pdf.setFillColor(248, 251, 251);
      this.pdf.setDrawColor(MINT_R, MINT_G, MINT_B);
      this.pdf.setLineWidth(0.3);
      const blockH = 12 + result.recommendations.length * 5;
      this.pdf.roundedRect(this.margin, this.currentY, this.contentWidth, blockH, 2, 2, 'FD');

      this.pdf.setFontSize(8.5);
      this.pdf.setFont('helvetica', 'bold');
      this.pdf.setTextColor(TEAL_R, TEAL_G, TEAL_B);
      this.pdf.text(`Analyse ${index + 1}`, this.margin + 4, this.currentY + 6);

      this.pdf.setTextColor(40, 40, 40);
      this.pdf.setFont('helvetica', 'normal');
      this.pdf.setFontSize(8);
      this.pdf.text(`Material: ${result.material}`, this.margin + 45, this.currentY + 6);
      this.pdf.text(`Zustand: ${result.condition}`, this.margin + 115, this.currentY + 6);

      let ry = this.currentY + 11.5;
      result.recommendations.forEach(rec => {
        this.pdf.setTextColor(60, 60, 60);
        this.pdf.text(`• ${rec}`, this.margin + 8, ry);
        ry += 5;
      });

      this.currentY += blockH + 5;
    });
  }

  private addNotes(notes: string) {
    this.addSectionHeader('Hinweise & Bemerkungen');

    this.pdf.setFont('helvetica', 'normal');
    this.pdf.setFontSize(9);
    this.pdf.setTextColor(50, 50, 50);

    const lines = this.pdf.splitTextToSize(notes, this.contentWidth - 4);
    lines.forEach((line: string) => {
      this.checkPageBreak(6);
      this.pdf.text(line, this.margin + 2, this.currentY);
      this.currentY += 5;
    });

    this.currentY += 6;
  }

  private addFooterAllPages() {
    const totalPages = this.pdf.getNumberOfPages();
    const cx = this.pageWidth / 2;

    // Partner-Logos im Footer: "Gemeinsam stark" als Überschrift, darunter
    // Hofer (Fassade & Betonsanierung) und Maler Ortner nebeneinander, jeweils
    // im natürlichen Seitenverhältnis skaliert, damit nichts verzerrt erscheint.
    const labelY = this.pageHeight - 28;
    const logoRowY = this.pageHeight - 25;
    const logoH = 9;
    const gap = 8;

    const hoferW = this.hoferLogoAspect ? logoH * this.hoferLogoAspect : 28;
    const ortnerW = this.ortnerLogoAspect ? logoH * this.ortnerLogoAspect : 18;
    const combinedLogoW = hoferW + gap + ortnerW;

    for (let p = 1; p <= totalPages; p++) {
      this.pdf.setPage(p);

      this.pdf.setDrawColor(TEAL_R, TEAL_G, TEAL_B);
      this.pdf.setLineWidth(0.5);
      this.pdf.line(this.margin, this.pageHeight - 32, this.pageWidth - this.margin, this.pageHeight - 32);

      this.pdf.setFont('helvetica', 'bold');
      this.pdf.setFontSize(8);
      this.pdf.setTextColor(TEAL_R, TEAL_G, TEAL_B);
      this.pdf.text('Gemeinsam stark', cx, labelY, { align: 'center' });

      const rowStartX = cx - combinedLogoW / 2;

      const renderLogo = (dataUrl: string | null, x: number, w: number, label: string) => {
        if (dataUrl) {
          try {
            this.pdf.addImage(dataUrl, getImageFormat(dataUrl), x, logoRowY, w, logoH);
            return;
          } catch { /* fall through to text fallback */ }
        }
        this.pdf.setFont('helvetica', 'normal');
        this.pdf.setFontSize(6);
        this.pdf.setTextColor(120, 120, 120);
        this.pdf.text(label, x + w / 2, logoRowY + logoH / 2, { align: 'center' });
      };

      renderLogo(this.hoferLogoDataUrl, rowStartX, hoferW, 'Hofer Fassade & Betonsanierung');
      renderLogo(this.ortnerLogoDataUrl, rowStartX + hoferW + gap, ortnerW, 'Maler Ortner');

      this.pdf.setDrawColor(200, 210, 210);
      this.pdf.setLineWidth(0.2);
      this.pdf.line(this.margin, this.pageHeight - 14, this.pageWidth - this.margin, this.pageHeight - 14);

      this.pdf.setFont('helvetica', 'normal');
      this.pdf.setFontSize(6.5);
      this.pdf.setTextColor(100, 110, 110);
      const footerLines = getCompanyContactLines();
      const footerGap = 3;
      const lastLineY = this.pageHeight - 8;
      const firstLineY = lastLineY - (footerLines.length - 1) * footerGap;
      footerLines.forEach((line, i) => {
        this.pdf.text(line, cx, firstLineY + i * footerGap, { align: 'center' });
      });

      this.pdf.setTextColor(140, 140, 140);
      this.pdf.text(
        `Seite ${p} / ${totalPages}`,
        this.pageWidth - this.margin,
        lastLineY,
        { align: 'right' }
      );
    }
  }

  private async compressImage(src: string, maxSize = 1024, quality = 0.8): Promise<string> {
    try {
      return await compressImageSrc(src, maxSize, quality);
    } catch (error) {
      console.warn("Bildkomprimierung fehlgeschlagen, nutze Original:", error);
      return src;
    }
  }
}
